import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, ExternalLink, Pin } from "lucide-react";
import { fetchNews, trackNewsClick } from "@/lib/api";

const SWIPE_THRESHOLD = 60; // px – past this, snap to next/prev slide

// Fisher-Yates shuffle — returns a new array; pinned items stay at the top so
// the admin's chosen highlight is always first, but the rest are mixed each
// page load so visitors see a different mix every visit.
function shuffleAds(list) {
    if (!Array.isArray(list) || list.length <= 1) return list || [];
    const pinned = list.filter((x) => x && x.is_pinned);
    const rest = list.filter((x) => !x || !x.is_pinned).slice();
    for (let i = rest.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    return [...pinned, ...rest];
}

export default function NewsList({ category, limit = 6, showImage = true }) {
    const [items, setItems] = useState(null); // null = loading, [] = loaded empty
    const [copiedId, setCopiedId] = useState(null);
    // Per-ad currently visible image index (in-place carousel)
    const [imgIdx, setImgIdx] = useState({});
    // Shuffle once per unique SET of ad IDs — not on every items reference
    // change. Without this, each lazy-loaded image triggers a reshuffle and
    // the cards visibly jump around on first load.
    const itemsKey = useMemo(() => {
        if (!Array.isArray(items)) return "";
        return items.map((x) => x.id).sort().join(",");
    }, [items]);
    // Stable ID order — recomputes ONLY when the set of IDs changes.
    const order = useMemo(
        () => shuffleAds(items || []).map((x) => x.id),
        [itemsKey] // eslint-disable-line react-hooks/exhaustive-deps
    );
    // Render list — maps the stable order to the CURRENT items so lazy-loaded
    // images appear on screen without ever reshuffling cards.
    const shuffled = useMemo(() => {
        if (!Array.isArray(items)) return [];
        const byId = Object.fromEntries(items.map((x) => [x.id, x]));
        return order.map((id) => byId[id]).filter(Boolean);
    }, [order, items]);

    useEffect(() => {
        let alive = true;
        const cacheKey = "__news_cache_v4__";
        try {
            const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
            if (cached && Array.isArray(cached.items)) setItems(cached.items);
        } catch {}
        const prefetched = typeof window !== "undefined" && window.__newsPromise;
        if (prefetched) window.__newsPromise = null;
        (prefetched || fetchNews())
            .then((data) => {
                if (!alive) return;
                if (!data || !Array.isArray(data.items)) {
                    setItems((prev) => prev || []);
                    return;
                }
                try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch {}
                setItems(data.items);
            })
            .catch(() => alive && setItems((prev) => prev || []));
        return () => {
            alive = false;
        };
    }, [category]);

    const setIdx = useCallback((adId, updater) =>
        setImgIdx((prev) => {
            const current = prev[adId] || 0;
            const next = typeof updater === "function" ? updater(current) : updater;
            return { ...prev, [adId]: next };
        }), []);

    const copyCode = async (e, item) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            await navigator.clipboard.writeText(item.code);
            setCopiedId(item.id);
            // Count copying the code as an engagement — same as clicking the link.
            trackNewsClick(item.id);
            toast.success(`تم نسخ الكود: ${item.code}`);
            setTimeout(() => setCopiedId(null), 2000);
        } catch {
            toast.error("تعذّر النسخ");
        }
    };

    const openLink = (ad) => {
        const hasLink = !!(ad.link && ad.link.trim());
        if (hasLink) {
            trackNewsClick(ad.id);
            window.open(ad.link, "_blank", "noopener,noreferrer");
        }
    };

    // Loading skeleton – shows immediately so the section is never empty
    if (items === null) {
        return (
            <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                data-testid="news-list-loading"
            >
                {Array.from({ length: Math.min(limit, 3) }).map((_, i) => (
                    <div
                        key={i}
                        className="bg-zinc-50 overflow-hidden animate-pulse"
                    >
                        <div className="aspect-[16/10] bg-zinc-200" />
                        <div className="p-6 space-y-3">
                            <div className="h-3 w-16 bg-zinc-200 rounded" />
                            <div className="h-5 w-3/4 bg-zinc-200 rounded" />
                            <div className="h-4 w-full bg-zinc-200 rounded" />
                            <div className="h-4 w-2/3 bg-zinc-200 rounded" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start"
            data-testid="news-list"
        >
            {shuffled.slice(0, limit).map((n) => {
                const gallery =
                    n.images && n.images.length > 0
                        ? n.images
                        : n.image_url
                        ? [n.image_url]
                        : [];
                const fits = n.image_fits || [];
                const currentIdx = imgIdx[n.id] || 0;
                const hasLink = !!(n.link && n.link.trim());

                return (
                    <article
                        key={n.id}
                        data-testid={`news-card-${n.slug}`}
                        className="bg-zinc-50 group hover:bg-zinc-100 transition-colors overflow-hidden"
                    >
                        {showImage && gallery.length > 0 && (
                            <SwipeableImage
                                ad={n}
                                images={gallery}
                                fits={fits}
                                currentIdx={currentIdx}
                                onChange={(next) => setIdx(n.id, next)}
                            />
                        )}
                        <div
                            className={`p-6 ${hasLink ? "cursor-pointer" : ""}`}
                            onClick={() => hasLink && openLink(n)}
                            data-testid={`news-body-${n.slug}`}
                        >
                            <span className="text-xs uppercase tracking-widest text-[#4FB8B0] font-bold">
                                إعلان
                            </span>
                            <h3 className="font-heading text-xl font-bold text-black mt-2 leading-snug group-hover:underline decoration-[#4FB8B0] underline-offset-4">
                                {n.title}
                            </h3>
                            {n.summary && (
                                <p className="text-sm text-zinc-600 mt-3 leading-relaxed line-clamp-3">
                                    {n.summary}
                                </p>
                            )}
                            <div className="flex items-center gap-2 mt-4 flex-wrap">
                                {n.code && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-zinc-700">كود المنتج:</span>
                                        <button
                                            onClick={(e) => copyCode(e, n)}
                                            data-testid={`news-code-${n.slug}`}
                                            className="inline-flex items-center gap-1.5 bg-[#F5FBFA] border border-dashed border-[#4FB8B0] rounded px-2.5 py-1 text-xs font-numeric font-bold text-[#1E5E5A] hover:bg-[#4FB8B0]/20"
                                        >
                                            {copiedId === n.id ? (
                                                <Check size={12} className="text-emerald-600" />
                                            ) : (
                                                <Copy size={12} />
                                            )}
                                            <span>{n.code}</span>
                                        </button>
                                    </div>
                                )}
                                {hasLink && (
                                    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#4FB8B0]">
                                        <ExternalLink size={12} />
                                        اضغط على النص للانتقال
                                    </span>
                                )}
                            </div>
                        </div>
                    </article>
                );
            })}
        </div>
    );
}

/**
 * One-image-at-a-time carousel with finger-follow swipe.
 * - Each image is rendered at its own natural aspect ratio (no letterboxing / no cropping).
 * - Card height animates smoothly between images.
 * - Adjacent images are absolutely positioned so the finger drag reveals them.
 * - All images preload eagerly so swipe is instant.
 */
function SwipeableImage({ ad, images, fits = [], currentIdx, onChange }) {
    const total = images.length;
    const containerRef = useRef(null);
    const startX = useRef(0);
    const startY = useRef(0);
    const dragging = useRef(false);
    const axisLocked = useRef(null);
    const [dragDx, setDragDx] = useState(0);
    const [snapping, setSnapping] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);

    // Sync container width with layout, synchronously before paint to avoid a 0-width first frame.
    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        const update = () => setContainerWidth(el.clientWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Auto-rotate every 2s in sync across all cards — they all flip together.
    const [paused, setPaused] = useState(false);
    const currentIdxRef = useRef(currentIdx);
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        currentIdxRef.current = currentIdx;
    }, [currentIdx]);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    useEffect(() => {
        if (total < 2 || paused) return;
        // Anchor all cards to a shared wall-clock so they flip in unison even
        // if they mount at different moments (e.g. after lazy image load).
        const now = Date.now();
        const untilNextTick = 2000 - (now % 2000);
        let intervalId;
        const timeoutId = setTimeout(() => {
            const tick = () => onChangeRef.current((currentIdxRef.current + 1) % total);
            tick();
            intervalId = setInterval(tick, 2000);
        }, untilNextTick);
        return () => {
            clearTimeout(timeoutId);
            if (intervalId) clearInterval(intervalId);
        };
    }, [total, paused]);

    const clampIdx = (i) => Math.max(0, Math.min(total - 1, i));

    const onPointerDown = (e) => {
        if (total < 2) return;
        if (e.pointerType === "mouse" && e.button !== 0) return;
        startX.current = e.clientX;
        startY.current = e.clientY;
        dragging.current = true;
        axisLocked.current = null;
        setSnapping(false);
        setPaused(true);
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {}
    };

    const onPointerMove = (e) => {
        if (!dragging.current) return;
        const dx = e.clientX - startX.current;
        const dy = e.clientY - startY.current;
        if (!axisLocked.current) {
            if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
                axisLocked.current = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
            }
        }
        if (axisLocked.current === "x") {
            setDragDx(dx);
        }
    };

    const onPointerUp = () => {
        if (!dragging.current) return;
        const dx = dragDx;
        dragging.current = false;
        setSnapping(true);
        setDragDx(0);
        // Resume auto-rotate a moment after the user lets go.
        setTimeout(() => setPaused(false), 3000);
        if (axisLocked.current !== "x") return;
        if (Math.abs(dx) < SWIPE_THRESHOLD) return;
        onChange(clampIdx(currentIdx + (dx < 0 ? 1 : -1)));
    };

    const fitFor = (i) => (fits[i] === "contain" ? "object-contain" : "object-cover");
    const currentSrc = images[currentIdx];
    // For the incoming (adjacent) image we reveal during a drag.
    const adjacentIdx = dragDx < 0 ? currentIdx + 1 : currentIdx - 1;
    const adjacentSrc = adjacentIdx >= 0 && adjacentIdx < total ? images[adjacentIdx] : null;

    return (
        <div
            ref={containerRef}
            className="bg-zinc-100 overflow-hidden relative select-none w-full max-w-[260px] mx-auto aspect-square rounded-md mt-4"
            data-testid={`news-image-${ad.slug}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            style={{
                cursor: total > 1 ? (dragging.current ? "grabbing" : "grab") : "default",
                touchAction: "pan-y",
            }}
        >
            {/* Current image – translates with the finger while dragging */}
            <img
                src={currentSrc}
                alt={ad.title}
                loading="lazy"
                decoding="async"
                draggable={false}
                className={`absolute inset-0 w-full h-full ${fitFor(currentIdx)} pointer-events-none will-change-transform bg-zinc-100`}
                style={{
                    transform: `translate3d(${dragDx}px, 0, 0)`,
                    transition: snapping ? "transform 260ms cubic-bezier(0.22, 1, 0.36, 1)" : "none",
                }}
            />

            {/* Adjacent image reveal during drag (positioned just off-screen in the drag direction) */}
            {adjacentSrc && dragDx !== 0 && containerWidth > 0 && (
                <img
                    src={adjacentSrc}
                    alt=""
                    loading="eager"
                    draggable={false}
                    className={`absolute inset-0 w-full h-full ${fitFor(adjacentIdx)} pointer-events-none will-change-transform bg-zinc-100`}
                    style={{
                        transform: `translate3d(${dragDx + (dragDx < 0 ? containerWidth : -containerWidth)}px, 0, 0)`,
                    }}
                />
            )}

            {total > 1 && (
                <img src={images[(currentIdx + 1) % total]} alt="" aria-hidden="true" className="hidden" />
            )}

            {ad.is_pinned && (
                <span className="absolute top-3 end-3 inline-flex items-center gap-1 bg-[#4FB8B0] text-white text-[11px] font-bold px-2 py-1 rounded-full shadow pointer-events-none z-10">
                    <Pin size={12} />
                    مثبّت
                </span>
            )}

            {total > 1 && (
                <>
                    <span className="absolute top-3 start-3 inline-flex items-center gap-1 bg-white/90 text-black text-[11px] font-bold px-2 py-1 rounded-full backdrop-blur pointer-events-none z-10">
                        {currentIdx + 1} / {total}
                    </span>
                    <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5 pointer-events-none z-10">
                        {images.map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === currentIdx
                                        ? "bg-white shadow w-6"
                                        : "bg-white/60 w-1.5"
                                }`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
